import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for debounced autosave with stale-closure protection.
 * Uses refs to ensure the debounce timer always captures the freshest state.
 */
export function useAutoSave({ selectedNote, selectedNoteId, editor, updateNote }) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const [userEditedTitle, setUserEditedTitle] = useState(false);

  const saveTimerRef = useRef(null);
  const titleRef = useRef('');
  const userEditedTitleRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Keep userEditedTitleRef in sync
  useEffect(() => {
    userEditedTitleRef.current = userEditedTitle;
  }, [userEditedTitle]);

  // Ref to track the current note ID for flush-on-navigate
  const currentNoteIdRef = useRef(null);

  // Helper: generate auto-title
  const generateAutoTitle = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `Note on ${year}-${month}-${day} at ${hours}:${minutes}`;
  };

  // Load note content when selection changes
  useEffect(() => {
    // Flush any pending save for the PREVIOUS note before switching
    if (saveTimerRef.current && currentNoteIdRef.current && editor) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      const prevId = currentNoteIdRef.current;
      let prevTitle = titleRef.current;
      try {
        const prevContent = editor.storage.markdown.getMarkdown();
        // Apply autotitle if needed
        if (!userEditedTitleRef.current && !prevTitle.trim() && prevContent.trim()) {
          prevTitle = generateAutoTitle();
        }
        updateNote(prevId, { title: prevTitle, content: prevContent });
      } catch { /* editor may already be updating */ }
    }

    currentNoteIdRef.current = selectedNoteId;

    if (selectedNote && editor) {
      // Don't load encrypted content into editor if decryption failed
      if (selectedNote._decryptFailed || selectedNote._needsDecryption) {
        isLoadingRef.current = true;
        editor.commands.setContent('');
        Promise.resolve().then(() => { isLoadingRef.current = false; });
        setSaved(true);
        return;
      }
      const noteTitle = selectedNote.title || '';
      setTitle(noteTitle);
      titleRef.current = noteTitle;
      const hasExistingTitle = selectedNote.title &&
        selectedNote.title.trim() !== '' &&
        selectedNote.title !== 'Untitled';
      setUserEditedTitle(hasExistingTitle);
      userEditedTitleRef.current = hasExistingTitle;
      const content = selectedNote.content || '';
      // Suppress onUpdate firing during content load
      isLoadingRef.current = true;
      editor.commands.setContent(content);
      // Clear flag after microtask so onUpdate has fired
      Promise.resolve().then(() => { isLoadingRef.current = false; });
      setSaved(true);
    }
  }, [selectedNote?.id, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount — flush any pending save
  useEffect(() => {
    return () => {
      if (saveTimerRef.current && currentNoteIdRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const saveNote = useCallback(async (newTitle, newContent) => {
    if (!selectedNoteId) return;
    setSaving(true);
    try {
      let titleToSave = newTitle;
      if (!userEditedTitleRef.current && !newTitle.trim() && newContent.trim()) {
        titleToSave = generateAutoTitle();
        setTitle(titleToSave);
        titleRef.current = titleToSave;
        setUserEditedTitle(true);
        userEditedTitleRef.current = true;
      }
      await updateNote(selectedNoteId, { title: titleToSave, content: newContent });
      setSaved(true);
    } catch { /* handled in context */ }
    setSaving(false);
  }, [selectedNoteId, updateNote]);

  const handleTitleChange = useCallback((newTitle) => {
    setTitle(newTitle);
    titleRef.current = newTitle;
    if (newTitle.trim()) {
      setUserEditedTitle(true);
      userEditedTitleRef.current = true;
    }
    setSaved(false);
    clearTimeout(saveTimerRef.current);
    const content = editor ? editor.storage.markdown.getMarkdown() : '';
    saveTimerRef.current = setTimeout(() => saveNote(newTitle, content), 1500);
  }, [editor, saveNote]);

  const handleContentChange = useCallback((newContent) => {
    // Skip saves triggered by loading note content into editor
    if (isLoadingRef.current) return;
    setSaved(false);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveNote(titleRef.current, newContent), 1500);
  }, [saveNote]);

  const handleForceSave = useCallback(() => {
    if (!selectedNoteId || !editor) return;
    const content = editor.storage.markdown.getMarkdown();
    saveNote(titleRef.current, content);
  }, [selectedNoteId, editor, saveNote]);

  return { title, setTitle, saving, saved, handleTitleChange, handleContentChange, handleForceSave };
}
